import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useLiquidityCachePool } from '../hooks/useLiquidityCachePool';
import { PoolStats } from '../components/PoolStats';
import { TradePanel } from '../components/TradePanel';
import { InvestPanel } from '../components/InvestPanel';
import { WithdrawPanel } from '../components/WithdrawPanel';
import { SimulatedBadge } from '../components/PoolCard';
import { api } from '@/lib/api';

export function PoolDetailPage() {
  const { poolId } = useParams<{ poolId: string }>();
  const { pool, loading, error, refresh } = useLiquidityCachePool(poolId);
  const [userLpShares, setUserLpShares] = useState('0');

  const fetchPosition = useCallback(async () => {
    if (!poolId) return;
    try {
      const result = await api.getLiquidityCachePosition(poolId);
      setUserLpShares(result.position?.lpShares ?? '0');
    } catch {
      // Not logged in or no position
    }
  }, [poolId]);

  useEffect(() => {
    fetchPosition();
  }, [fetchPosition]);

  const handleActionComplete = () => {
    refresh();
    fetchPosition();
  };

  if (loading) return <p className="text-muted-foreground">Loading cache...</p>;
  if (error) return <p className="text-red-600">{error}</p>;
  if (!pool) return <p className="text-muted-foreground">Cache not found.</p>;

  const hasExternal = pool.tokenA.source === 'external' || pool.tokenB.source === 'external';

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <h1 className="text-2xl font-bold">{pool.name}</h1>
        <span className="text-muted-foreground">
          {pool.tokenA.symbol} / {pool.tokenB.symbol}
        </span>
        {hasExternal && <SimulatedBadge />}
      </div>

      {pool.description && <p className="text-muted-foreground">{pool.description}</p>}

      <PoolStats pool={pool} />

      <Tabs defaultValue="trade">
        <TabsList>
          <TabsTrigger value="trade">Trade</TabsTrigger>
          <TabsTrigger value="invest">Invest</TabsTrigger>
          <TabsTrigger value="withdraw">Withdraw</TabsTrigger>
        </TabsList>
        <TabsContent value="trade" className="mt-4">
          <TradePanel pool={pool} onTradeComplete={handleActionComplete} />
        </TabsContent>
        <TabsContent value="invest" className="mt-4">
          <InvestPanel pool={pool} onInvestComplete={handleActionComplete} />
        </TabsContent>
        <TabsContent value="withdraw" className="mt-4">
          <WithdrawPanel
            pool={pool}
            userLpShares={userLpShares}
            onWithdrawComplete={handleActionComplete}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
